/**
 * Schémas Zod ventes — devis (épopée E11).
 * Référence : document 06 § 6.4 ; document 09 § 11.
 */
import { z } from 'zod';
import { QUOTE_DISPLAY_MODES, QUOTE_LINE_TYPES, QUOTE_STATUSES } from '../enums/quote.enum.js';
import { paginationQuerySchema, searchSchema, uuidSchema } from './common.schema.js';
const optionalText = (max) => z.string().trim().max(max).nullable().optional();
const optionalDateSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, {
    message: 'Date invalide (format attendu : AAAA-MM-JJ).',
})
    .nullable()
    .optional();
/** Montant ou quantité décimal, accepté en nombre ou en chaîne, jamais négatif. */
const nonNegativeDecimal = (message) => z
    .union([z.number(), z.string().regex(/^\d{1,11}(\.\d{1,4})?$/, { message })])
    .refine((value) => Number(value) >= 0, {
    message: 'La valeur ne peut pas être négative.',
});
/** Pourcentage borné entre 0 et `max`, accepté en nombre ou en chaîne. */
const percentSchema = (max) => z
    .union([
    z.number(),
    z.string().regex(/^\d{1,3}(\.\d{1,2})?$/, { message: 'Pourcentage invalide.' }),
])
    .refine((value) => Number(value) >= 0 && Number(value) <= max, {
    message: `Le pourcentage doit être compris entre 0 et ${max}.`,
});
/** Ligne de devis en entrée d'écriture. */
export const quoteLineInputSchema = z.object({
    lineType: z.enum(QUOTE_LINE_TYPES).default('item'),
    itemId: uuidSchema.nullable().optional(),
    reference: optionalText(40),
    label: z.string().trim().min(1).max(255),
    longLabel: optionalText(255),
    description: optionalText(4000),
    quantity: nonNegativeDecimal('Quantité invalide.').optional(),
    unitId: uuidSchema.nullable().optional(),
    unitPriceHt: nonNegativeDecimal('Prix unitaire invalide.').optional(),
    discountPercent: percentSchema(100).nullable().optional(),
    discountAmount: nonNegativeDecimal('Remise de ligne invalide.').nullable().optional(),
    vatRateId: uuidSchema.nullable().optional(),
    vatRatePercent: percentSchema(100).nullable().optional(),
    isOptional: z.boolean().optional(),
    isSelected: z.boolean().optional(),
});
const quoteBaseSchema = {
    clientId: uuidSchema.nullable().optional(),
    prospectId: uuidSchema.nullable().optional(),
    contactId: uuidSchema.nullable().optional(),
    opportunityId: uuidSchema.nullable().optional(),
    issuedAt: optionalDateSchema,
    validUntil: optionalDateSchema,
    currency: z.string().trim().length(3).optional(),
    displayMode: z.enum(QUOTE_DISPLAY_MODES).optional(),
    subject: optionalText(255),
    globalDiscountPercent: percentSchema(100).nullable().optional(),
    globalDiscountAmount: nonNegativeDecimal('Remise globale invalide.').nullable().optional(),
    depositPercent: percentSchema(100).nullable().optional(),
    paymentTermsId: uuidSchema.nullable().optional(),
    paymentMethodId: uuidSchema.nullable().optional(),
    notes: optionalText(4000),
    internalNotes: optionalText(4000),
    lines: z.array(quoteLineInputSchema).max(500).optional(),
};
/** Paramètres de `GET /quotes`. */
export const quoteListQuerySchema = paginationQuerySchema.extend({
    search: searchSchema,
    filter: z
        .object({
        status: z.enum(QUOTE_STATUSES).optional(),
        clientId: uuidSchema.optional(),
        opportunityId: uuidSchema.optional(),
    })
        .optional(),
});
/** Un devis porte un tiers : un client ou un prospect, jamais les deux ni aucun. */
const PARTY_ERROR = 'Sélectionnez un client ou un prospect, et un seul.';
function hasClient(value) {
    return value.clientId !== undefined && value.clientId !== null;
}
function hasProspect(value) {
    return value.prospectId !== undefined && value.prospectId !== null;
}
/** Corps de `POST /quotes`. */
export const quoteCreateSchema = z
    .object(quoteBaseSchema)
    .refine((value) => hasClient(value) !== hasProspect(value), {
    message: PARTY_ERROR,
    path: ['clientId'],
});
/** Corps de `PATCH /quotes/:id`. */
export const quoteUpdateSchema = z
    .object(quoteBaseSchema)
    .partial()
    .refine((value) => Object.values(value).some((field) => field !== undefined), {
    message: 'Aucun champ à mettre à jour.',
})
    .refine((value) => {
    // Le tiers reste inchangé tant qu'aucune des deux clés n'est transmise.
    if (value.clientId === undefined && value.prospectId === undefined) {
        return true;
    }
    return hasClient(value) !== hasProspect(value);
}, { message: PARTY_ERROR, path: ['clientId'] });
/** Corps de `PATCH /quotes/:id/status` (transitions d'état du devis). */
export const quoteStatusUpdateSchema = z.object({
    status: z.enum(QUOTE_STATUSES),
    refusalReason: optionalText(255),
});
//# sourceMappingURL=quote.schema.js.map